import {
  Color,
  Vector3,
  type Texture,
  type WebGLProgramParametersWithUniforms,
} from "three";
import alphaMapFrag from "../shaders/alpha_map.frag";
import dissolveMainFrag from "../shaders/dissolve_main.frag";
import emissiveMapFrag from "../shaders/emissive_map.frag";
import emissiveTonemappingFrag from "../shaders/emissive_tonemapping.frag";
import glitterMainFrag from "../shaders/glitter_main.frag";
import glitterMainVert from "../shaders/glitter_main.vert";
import glitterParsFrag from "../shaders/glitter_pars.frag";
import glitterParsVert from "../shaders/glitter_pars.vert";
import marbleDefsVert from "../shaders/marble_defs.vert";
import marbleMapFrag from "../shaders/marble_map.frag";
import marbleParsFrag from "../shaders/marble_pars.frag";
import marblePositionsVert from "../shaders/marble_position.vert";
import numberMainFrag from "../shaders/number_main.frag";
import numberMainVert from "../shaders/number_main.vert";
import numberNormalFrag from "../shaders/number_normal.frag";
import numberParsFrag from "../shaders/number_pars.frag";
import numberParsVert from "../shaders/number_pars.vert";

/**
 * This modifies the ThreeJS meshPhysicalMaterial shaders
 * @see https://github.com/mrdoob/three.js/blob/90c34c0ebe693ed524079ad97c56340d7c40483b/src/renderers/shaders/ShaderLib/meshphysical.glsl.js
 */

export const updateShader = (
  shader: WebGLProgramParametersWithUniforms,
  useNumbers: boolean,
  values: number,
  dissolveTexture: Texture,
  numberTexture: Texture,
  useDissolve: boolean,
  numberColor?: string,
  numberHighlightColor?: string,
  numberBumpScale?: number,
  numbersMode?: number,
  marbleAmplitude?: number,
  marbleTiming?: number,
  marbleZoom?: number,
  glitterScale?: number,
  glitterPatternTexture?: Texture,
  glitterNoiseTexture?: Texture,
  instanced?: boolean,
) => {
  const vertexDeclarations: string[] = [];
  const fragmentDeclarations: string[] = [];
  const addUniform = (name: string, type: string, value: unknown) => {
    vertexDeclarations.push(`uniform ${type} ${name};`);
    fragmentDeclarations.push(`uniform ${type} ${name};`);
    shader.uniforms[name] = { value };
  };

  addUniform("iGlobalTime", "float", 0.0);
  addUniform("dieType", "lowp int", values);

  if (instanced) {
    vertexDeclarations.push(`
      attribute float instanceLastEvent;
      attribute float instanceLastEventTime;
      attribute float instanceDieValue;
      attribute vec3 instanceTopFaceNormal;
      attribute float instanceDissolveValue;
      varying float vInstanceLastEvent;
      varying float vInstanceLastEventTime;
      varying float vInstanceDieValue;
      varying vec3 vInstanceTopFaceNormal;
      varying float vInstanceDissolveValue;
      #define topFaceNormal instanceTopFaceNormal
    `);

    fragmentDeclarations.push(`
      varying float vInstanceLastEvent;
      varying float vInstanceLastEventTime;
      varying float vInstanceDieValue;
      varying vec3 vInstanceTopFaceNormal;
      varying float vInstanceDissolveValue;
      #define lastEvent int(vInstanceLastEvent)
      #define lastEventTime vInstanceLastEventTime
      #define dieValue int(vInstanceDieValue)
      #define topFaceNormal vInstanceTopFaceNormal
      #define dissolveValue vInstanceDissolveValue
      #define numberAnimationTime (iGlobalTime - vInstanceLastEventTime)
    `);
  } else {
    // Events
    addUniform("lastEvent", "lowp int", 0);
    addUniform("lastEventTime", "float", 0.0);
    addUniform("dieValue", "lowp int", 0);
    addUniform("topFaceNormal", "vec3", new Vector3(0, 0, 0));
    fragmentDeclarations.push("#define numberAnimationTime iGlobalTime");
  }

  const customDefinitions = [];

  if (useDissolve) {
    customDefinitions.push("#define USE_DISSOLVE");
    addUniform("dissolveTexture", "sampler2D", dissolveTexture);

    if (!instanced) {
      addUniform("dissolveValue", "float", 0.0);
    }
  }

  if (useNumbers) {
    customDefinitions.push("#define USE_NUMBERS");
    addUniform("numberTexture", "sampler2D", numberTexture);
    addUniform("numberBumpScale", "float", -(numberBumpScale ?? 0.1));
    addUniform("numberColor", "vec3", new Color(numberColor));
    addUniform("numberHighlightColor", "vec3", new Color(numberHighlightColor));
    addUniform("numberFadeCutoff", "float", 1.0);
    addUniform("numbersMode", "lowp int", numbersMode ?? 1);
    addUniform("fadeSpeed", "float", 2.0);
  }

  // shader.map ensures we have an albedo (map) texture loaded to run the marbling effect
  if (shader.map && marbleAmplitude && marbleAmplitude > 0) {
    customDefinitions.push("#define USE_MARBLE");
    addUniform("marbleNoiseTexture", "sampler2D", dissolveTexture);
    addUniform("marbleAmplitude", "float", marbleAmplitude ?? 0.0);
    addUniform("marbleTiming", "float", marbleTiming ?? 0.025);
    addUniform("marbleZoom", "float", marbleZoom ?? 0.5);
  }

  addUniform("glitterScale", "float", glitterScale ?? 0.0);
  addUniform("glitterNoiseTexture", "sampler2D", glitterNoiseTexture ?? null);
  addUniform(
    "glitterPatternTexture",
    "sampler2D",
    glitterPatternTexture ?? null,
  );

  /** Vertex Shader */
  const common = "#include <common>";
  shader.vertexShader = shader.vertexShader.replace(
    common,
    `
      ${common}
      ${vertexDeclarations.join("\n")}
      ${numberParsVert}
      ${marbleDefsVert}
      ${glitterParsVert}
    `,
  );

  const worldPos = "#include <worldpos_vertex>";
  shader.vertexShader = shader.vertexShader.replace(
    worldPos,
    `
      ${worldPos}
      ${
        instanced
          ? `
            vInstanceLastEvent = instanceLastEvent;
            vInstanceLastEventTime = instanceLastEventTime;
            vInstanceDieValue = instanceDieValue;
            vInstanceTopFaceNormal = instanceTopFaceNormal;
            vInstanceDissolveValue = instanceDissolveValue;
          `
          : ""
      }
      ${numberMainVert}
      ${marblePositionsVert}
      ${glitterMainVert}
    `,
  );

  /** Fragment Shader */
  const commonFrag = "#include <common>";
  shader.fragmentShader = shader.fragmentShader.replace(
    commonFrag,
    `
      ${commonFrag}
      ${fragmentDeclarations.join("\n")}
      ${customDefinitions.join("\n")}
      ${numberParsFrag}
      ${marbleDefsVert}
      ${marbleParsFrag}
      ${glitterParsFrag}
    `,
  );

  const mapFrag = "#include <map_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    mapFrag,
    `
      ${mapFrag}
      ${marbleMapFrag}
    `,
  );

  const beforeLights = "#include <normal_fragment_maps>";
  shader.fragmentShader = shader.fragmentShader.replace(
    beforeLights,
    `
      ${beforeLights}
      ${numberNormalFrag}
    `,
  );

  const emissiveMap = "#include <emissivemap_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    emissiveMap,
    `
      ${emissiveMap}
      ${emissiveMapFrag}
    `,
  );

  const beforeTransmission = "#include <transmission_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    beforeTransmission,
    `
      ${numberMainFrag}
      ${beforeTransmission}
    `,
  );

  const tonemapping = "#include <tonemapping_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    tonemapping,
    `
      ${emissiveTonemappingFrag}
      ${tonemapping}
    `,
  );

  const pmAlpha = "#include <premultiplied_alpha_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    pmAlpha,
    `
      ${pmAlpha}
      ${glitterMainFrag}
    `,
  );

  const dithering = "#include <dithering_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    dithering,
    `
      ${dithering}
      ${dissolveMainFrag}
    `,
  );

  // Handles a difference between how babylon and three use alpha maps
  // Tested with Bigby's glyphs and most other working sets with opacity textures
  const alphaMap = "#include <alphamap_fragment>";
  shader.fragmentShader = shader.fragmentShader.replace(
    alphaMap,
    `
      ${alphaMapFrag}
    `,
  );
};
